import {
  Accordion,
  Badge,
  Button,
  cn,
  Dialog,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Draggable,
  type DragMoveEvent,
  Icon,
  Text,
} from '@risksmart-app/atomic-ui';
import type { ReactNode, Ref } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { FieldEditorValues, SectionEditorValues } from '../config';
import type {
  DEFAULT_LANG,
  FieldSummary,
  UniqueIdentifier,
} from '../constants';
import {
  formEditorAnchorProps,
  useFormEditorScrollAnchor,
} from '../scrollAnchor';
import type { FormEditorPreviewData } from '../types';
import { useFormEditorDialogStore } from '../useFormEditorDialogStore';
import { FieldCard } from './FieldCard';
import { FormPreview, type FormPreviewHandle } from './FormPreview';
import { SectionAccordion } from './SectionAccordion';
import { SectionPreview } from './SectionPreview';
import { SlideOutDiscardButton } from './SlideOutDiscardButton';

export const FormEditorMainDialog = ({
  open,
  isDirty,
  onClose,
  onSave,
  saving,
  lang,
  containerOrder,
  containers,
  fields,
  sectionNames,
  fieldConfigs,
  sectionConfigs,
  renderPreview,
  disallowedSectionId,
  onMove,
  canMove,
  onDragEnd,
  onDragCancel,
  onReorderContainers,
  onEditField,
  onDeleteField,
  onEditSection,
  onDeleteSection,
  onAddField,
  onAddSection,
  fieldTypeOptions,
}: {
  open: boolean;
  isDirty: boolean;
  onClose: () => void;
  onSave: () => void;
  saving?: boolean;
  lang: typeof DEFAULT_LANG.formEditor;
  containerOrder: string[];
  containers: Record<string, string[]>;
  fields: Record<string, FieldSummary>;
  sectionNames: Record<string, string>;
  fieldConfigs: Record<string, FieldEditorValues>;
  sectionConfigs: Record<string, SectionEditorValues>;
  renderPreview?: (
    data: FormEditorPreviewData,
    opts: { ref: Ref<FormPreviewHandle>; onDataChange: () => void }
  ) => ReactNode;
  /**
   * Section id the user is currently hovering over with a field that
   * would create a circular dependency. When set, the section renders an
   * "invalid drop target" overlay.
   */
  disallowedSectionId?: string | null;
  onMove: (event: DragMoveEvent) => void;
  canMove?: (event: DragMoveEvent) => boolean;
  onDragEnd?: () => void;
  onDragCancel?: () => void;
  onReorderContainers: (ids: UniqueIdentifier[]) => void;
  onEditField: (fieldId: string) => void;
  onDeleteField: (sectionId: string, fieldId: string) => void;
  onEditSection: (sectionId: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onAddField: (sectionId: string) => void;
  onAddSection: () => void;
  fieldTypeOptions?: { value: string; label: string }[];
}) => {
  const openSections = useFormEditorDialogStore((s) => s.openSections);
  const setOpenSections = useFormEditorDialogStore((s) => s.setOpenSections);
  const hasDraggable = containerOrder.length > 0;

  const [previewing, setPreviewing] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [showDiscard, setShowDiscard] = useState(false);
  const previewRef = useRef<FormPreviewHandle>(null);
  const [validationStatus, setValidationStatus] = useState<
    'valid' | 'invalid' | null
  >(null);

  // `open` also goes false when the user steps into a sub-editor (the host
  // renders this with `open && activeView === 'form-editor'`) and the component
  // stays mounted, so an armed discard would otherwise still be waiting on
  // return.
  useEffect(() => {
    if (!open) {
      setPreviewing(false);
      setValidationStatus(null);
      setShowDiscard(false);
    }
  }, [open]);

  useEffect(() => {
    if (!isDirty) {
      setShowDiscard(false);
    }
  }, [isDirty]);

  useFormEditorScrollAnchor(open && !previewing);

  const exitPreview = () => {
    setPreviewing(false);
    setValidationStatus(null);
    setShowDiscard(false);
  };

  const enterPreview = () => {
    setPreviewKey((k) => k + 1);
    setShowDiscard(false);
    setPreviewing(true);
  };

  const handleCancelClick = () => {
    if (previewing) {
      exitPreview();
    } else if (isDirty) {
      setShowDiscard(!showDiscard);
    } else {
      onClose();
    }
  };

  // Stable identity so the preview's change subscription isn't torn down and
  // recreated on every render.
  const handlePreviewDataChange = useCallback(
    () => setValidationStatus(null),
    []
  );

  const hasConditionalLogic = (sectionId: string): boolean => {
    const isLogicEnabled = sectionConfigs[sectionId]?.conditionalLogicEnabled;
    const hasLogicRules =
      (sectionConfigs[sectionId]?.conditionalLogicRules?.length ?? 0) > 0;

    return !!(isLogicEnabled && hasLogicRules);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleCancelClick();
        }
      }}
      size={'xl'}
    >
      <DialogHeader>
        <div>
          <DialogTitle>{lang.title}</DialogTitle>
          <DialogDescription>{lang.description}</DialogDescription>
        </div>
        <div className={cn('flex items-center gap-2')}>
          {previewing ? (
            <Badge variant={'secondary'} border>
              <div className={cn('flex gap-1 items-center')}>
                <Icon name={'eye'} size={'xs'} />
                {'Preview mode'}
              </div>
            </Badge>
          ) : null}
          <Button
            className={cn('p-0 size-auto')}
            variant={'neutral'}
            style={'ghost'}
            size={'icon'}
            aria-label={'Close'}
            onClick={handleCancelClick}
          >
            <Icon name={'x'} size={'lg'} />
          </Button>
        </div>
      </DialogHeader>
      <Dialog.Body className={'flex flex-col gap-4 p-4'}>
        {previewing ? (
          renderPreview ? (
            renderPreview(
              {
                containerOrder,
                containers,
                fields,
                sectionNames,
                fieldConfigs,
                sectionConfigs,
              },
              {
                ref: previewRef,
                onDataChange: handlePreviewDataChange,
              }
            )
          ) : (
            <FormPreview
              key={previewKey}
              ref={previewRef}
              containerOrder={containerOrder}
              containers={containers}
              fields={fields}
              sectionNames={sectionNames}
              fieldConfigs={fieldConfigs}
              sectionConfigs={sectionConfigs}
              onDataChange={handlePreviewDataChange}
            />
          )
        ) : (
          <Accordion
            multiple
            value={openSections}
            onValueChange={setOpenSections}
          >
            {hasDraggable ? (
              <Draggable.Multi
                containers={containers}
                containerOrder={containerOrder}
                onMove={onMove}
                canMove={canMove}
                onReorderContainers={
                  onReorderContainers as
                    | ((ids: UniqueIdentifier[]) => void)
                    | undefined
                }
                onDragStart={(event) => {
                  if (String(event.active.id) in containers) {
                    setOpenSections([]);
                  }
                }}
                onDragEnd={onDragEnd}
                onDragCancel={onDragCancel}
                className={'flex flex-col gap-4'}
              >
                {containerOrder.map((sectionId) => (
                  <Draggable.Container
                    key={sectionId}
                    id={sectionId}
                    items={containers[sectionId]}
                    isSortable
                    {...formEditorAnchorProps(sectionId)}
                  >
                    <SectionAccordion
                      sectionId={sectionId}
                      title={sectionNames[sectionId] ?? sectionId}
                      fieldIds={containers[sectionId]}
                      containerIds={containerOrder}
                      fields={fields}
                      fieldConfigs={fieldConfigs}
                      hasConditionalLogic={hasConditionalLogic(sectionId)}
                      addFieldsLabel={lang.addField}
                      fieldTypeOptions={fieldTypeOptions}
                      disallowed={disallowedSectionId === sectionId}
                      onEditSection={() => onEditSection(sectionId)}
                      onDeleteSection={() => onDeleteSection(sectionId)}
                      onEditField={(fieldId) => onEditField(fieldId)}
                      onDeleteField={(fieldId) =>
                        onDeleteField(sectionId, fieldId)
                      }
                      onAddField={() => onAddField(sectionId)}
                    />
                  </Draggable.Container>
                ))}
                <Draggable.Overlay>
                  {(activeId) => {
                    const id = String(activeId);
                    if (id in containers) {
                      return <SectionPreview title={sectionNames[id] ?? id} />;
                    }
                    const field = fields[id];
                    if (field) {
                      const fieldConfig = fieldConfigs[id];

                      return (
                        <FieldCard
                          name={field.name}
                          type={field.type}
                          required={field.required}
                          readOnly={field.readOnly}
                          hidden={field.hidden}
                          hasConditionalLogic={
                            !!(
                              fieldConfig?.conditionalLogicEnabled &&
                              (fieldConfig.conditionalLogicRules?.length ?? 0) >
                                0
                            )
                          }
                          subtitle={field.subtitle}
                          fieldTypeOptions={fieldTypeOptions}
                        />
                      );
                    }

                    return null;
                  }}
                </Draggable.Overlay>
              </Draggable.Multi>
            ) : null}
            {lang.addSection ? (
              <Button
                style={'dashed-fill'}
                radius={'xl'}
                className={'w-full'}
                onClick={onAddSection}
              >
                <Icon name={'plus'} size={'sm'} />
                {lang.addSection}
              </Button>
            ) : null}
          </Accordion>
        )}
      </Dialog.Body>
      <Dialog.Footer className={'flex items-center justify-between'}>
        <div className={cn('flex items-center gap-2')}>
          {previewing ? (
            <Button
              variant={
                validationStatus === 'invalid'
                  ? 'destructive'
                  : validationStatus === 'valid'
                    ? 'success'
                    : 'default'
              }
              className={cn('transition-colors active:opacity-80')}
              onClick={async () => {
                const isValid = (await previewRef.current?.validate()) ?? false;
                setValidationStatus(isValid ? 'valid' : 'invalid');
              }}
            >
              {'Test validation'}
            </Button>
          ) : (
            <Button onClick={onSave} disabled={saving}>
              {saving ? 'Saving...' : lang.save}
            </Button>
          )}
          <Button
            variant={'neutral'}
            style={'outline'}
            onClick={handleCancelClick}
          >
            {lang.cancel}
          </Button>
          <SlideOutDiscardButton visible={showDiscard} onDiscard={onClose} />
        </div>
        <div className={cn('flex items-center gap-2')}>
          <Text
            preset={'body-md'}
            className={cn(
              'text-sm text-muted-foreground transition-opacity duration-300',
              isDirty ? 'opacity-100' : 'opacity-0'
            )}
            render={<span />}
          >
            {'You have unsaved changes'}
          </Text>
          <Button
            variant={'neutral'}
            style={'outline'}
            onClick={() => {
              if (previewing) {
                exitPreview();
              } else {
                enterPreview();
              }
            }}
          >
            <Icon name={previewing ? 'eye-off' : 'eye'} size={'lg'} />
            {previewing ? 'Close preview' : 'Preview'}
          </Button>
        </div>
      </Dialog.Footer>
    </Dialog>
  );
};
